import { useEffect, useState } from "react";
import api from "../lib/typedApi";
import { useAuth } from "../lib/auth";
import { Link } from "react-router-dom";
import { Avatar, NBCard, PageHeader } from "../components/UI";
import {
  Users, ListChecks, Calendar, ShoppingCart, PawPrint, Image as ImgIcon,
  TrendingUp, TrendingDown, Sparkles, ArrowRight
} from "lucide-react";

export default function Dashboard() {
  const { user } = useAuth();
  const [summary, setSummary] = useState(null);
  const [members, setMembers] = useState([]);
  const [todayTasks, setTodayTasks] = useState([]);
  const [upcomingEvents, setUpcomingEvents] = useState([]);

  useEffect(() => {
    const load = async () => {
      try {
        const [s, m, t, e] = await Promise.all([
          api.get("/api/dashboard/summary"),
          api.get("/api/members"),
          api.get("/api/tasks"),
          api.get("/api/events"),
        ]);
        setSummary(s.data);
        setMembers(m.data);
        setTodayTasks(t.data.filter((x) => !x.completed).slice(0, 5));
        setUpcomingEvents(e.data.slice(0, 4));
      } catch (err) {
        console.error(err);
      }
    };
    load();
  }, []);

  const stats = [
    { label: "Family", value: summary?.members ?? 0, icon: Users, color: "#E8B96B", to: "/members" },
    { label: "Open Tasks", value: summary?.open_tasks ?? 0, icon: ListChecks, color: "#A8BFD1", to: "/tasks" },
    { label: "Events", value: summary?.events ?? 0, icon: Calendar, color: "#E9B5A0", to: "/calendar" },
    { label: "Shopping", value: summary?.shopping_pending ?? 0, icon: ShoppingCart, color: "#B8CFA9", to: "/shopping" },
    { label: "Pets", value: summary?.pets ?? 0, icon: PawPrint, color: "#E8B96B", to: "/pets" },
    { label: "Photos", value: summary?.photos ?? 0, icon: ImgIcon, color: "#A8BFD1", to: "/photos" },
  ];

  const memberName = user?.member?.name?.split(" ")[0] || "there";

  return (
    <div data-testid="dashboard-page">
      <PageHeader
        title={`Hey, ${memberName} 👋`}
        subtitle={`Here's what's happening with the ${user?.family?.name || "family"} today.`}
      />

      {/* Marquee announcement */}
      <div className="border-y-2 border-black bg-[#E8B96B] py-2 mb-8 overflow-hidden">
        <div className="marquee whitespace-nowrap flex gap-12 font-black uppercase tracking-widest text-sm" style={{ fontFamily: "var(--hh-display)" }}>
          {Array(8).fill(0).map((_, i) => (
            <span key={i}>★ Stay organized ★ Family first ★ Make memories ★ Built with love</span>
          ))}
        </div>
      </div>

      {/* Stats grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
        {stats.map((s) => (
          <Link key={s.label} to={s.to} data-testid={`stat-${s.label.toLowerCase().replace(" ", "-")}`}>
            <NBCard color={s.color} className="!p-4">
              <s.icon strokeWidth={2.5} className="w-5 h-5 mb-2" />
              <p className="text-3xl font-black" style={{ fontFamily: "var(--hh-display)" }}>{s.value}</p>
              <p className="text-xs font-bold uppercase tracking-wider">{s.label}</p>
            </NBCard>
          </Link>
        ))}
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Family members */}
        <NBCard className="lg:col-span-1">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xl font-black" style={{ fontFamily: "var(--hh-display)" }}>Family</h3>
            <Link to="/members" className="text-xs font-black uppercase underline decoration-[3px] decoration-[#E8B96B] underline-offset-4">Manage</Link>
          </div>
          <div className="space-y-3">
            {members.length === 0 && <p className="text-sm text-neutral-600">No members yet</p>}
            {members.map((m) => (
              <div key={m.id} className="flex items-center gap-3" data-testid={`dashboard-member-${m.id}`}>
                <Avatar name={m.name} color={m.avatar_color} size={36} />
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-sm truncate">{m.name}</p>
                  <p className="text-xs text-neutral-600 truncate">{m.role}</p>
                </div>
              </div>
            ))}
          </div>
        </NBCard>

        {/* Today's tasks */}
        <NBCard className="lg:col-span-1">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xl font-black" style={{ fontFamily: "var(--hh-display)" }}>Open Tasks</h3>
            <Link to="/tasks" className="text-xs font-black uppercase underline decoration-[3px] decoration-[#A8BFD1] underline-offset-4">All</Link>
          </div>
          <div className="space-y-2">
            {todayTasks.length === 0 && <p className="text-sm text-neutral-600">All clear! No pending tasks.</p>}
            {todayTasks.map((t) => (
              <div key={t.id} className="flex items-start gap-3 p-2 border-2 border-black rounded-lg bg-[#F6EFE4]">
                <div className="w-4 h-4 rounded-full border-2 border-black mt-0.5 shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-sm truncate">{t.title}</p>
                  {t.due_date && <p className="text-xs text-neutral-600">Due {t.due_date}</p>}
                </div>
              </div>
            ))}
          </div>
        </NBCard>

        {/* Upcoming events */}
        <NBCard color="#E9B5A0" className="lg:col-span-1">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xl font-black" style={{ fontFamily: "var(--hh-display)" }}>Upcoming</h3>
            <Link to="/calendar" className="text-xs font-black uppercase underline decoration-[3px] decoration-black underline-offset-4">Calendar</Link>
          </div>
          <div className="space-y-2">
            {upcomingEvents.length === 0 && <p className="text-sm">No events scheduled</p>}
            {upcomingEvents.map((e) => (
              <div key={e.id} className="p-2 border-2 border-black rounded-lg bg-white">
                <p className="font-bold text-sm truncate">{e.title}</p>
                <p className="text-xs text-neutral-600">{e.start && new Date(e.start).toLocaleString()}</p>
              </div>
            ))}
          </div>
        </NBCard>

        {/* Budget */}
        <NBCard color="#B8CFA9" className="lg:col-span-1">
          <h3 className="text-xl font-black mb-3" style={{ fontFamily: "var(--hh-display)" }}>This Month</h3>
          <div className="grid grid-cols-2 gap-3">
            <div className="p-3 bg-white border-2 border-black rounded-lg">
              <TrendingUp strokeWidth={3} className="w-5 h-5 mb-1" />
              <p className="text-2xl font-black" style={{ fontFamily: "var(--hh-display)" }}>${(summary?.month_income ?? 0).toFixed(0)}</p>
              <p className="text-xs font-bold">Income</p>
            </div>
            <div className="p-3 bg-white border-2 border-black rounded-lg">
              <TrendingDown strokeWidth={3} className="w-5 h-5 mb-1" />
              <p className="text-2xl font-black" style={{ fontFamily: "var(--hh-display)" }}>${(summary?.month_expenses ?? 0).toFixed(0)}</p>
              <p className="text-xs font-bold">Expenses</p>
            </div>
          </div>
          <Link to="/budget" className="mt-3 inline-flex items-center gap-1 text-xs font-black uppercase underline decoration-[3px] decoration-black underline-offset-4">
            Track budget <ArrowRight strokeWidth={3} className="w-3 h-3" />
          </Link>
        </NBCard>

        {/* AI shortcut */}
        <NBCard color="#A8BFD1" className="lg:col-span-2">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-xl border-2 border-black bg-white flex items-center justify-center shrink-0">
              <Sparkles strokeWidth={3} />
            </div>
            <div className="flex-1">
              <h3 className="text-xl font-black mb-1" style={{ fontFamily: "var(--hh-display)" }}>Ask FamilyOS AI</h3>
              <p className="text-sm mb-3">Get weekly reminders, meal ideas, planning help, and more — powered by GPT.</p>
              <Link to="/ai" className="nb-btn bg-[#E8B96B] inline-block px-4 py-2 text-sm" data-testid="dashboard-ai-link">
                Open Assistant →
              </Link>
            </div>
          </div>
        </NBCard>
      </div>
    </div>
  );
}
